import type { AdminInspectorDetailDto } from '@carcheck/shared';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoGrid, InfoItem, SectionTitle } from './InfoBlocks';
import { KycSessionBadge } from './InspectorBadges';
import { tokens } from '../../../design';
import { formatDate } from '../../../utils/format';

/**
 * KYC tab of the inspector detail drawer (AUT-125). Mirrors the mockup's
 * "Верификация личности" + "Документы" sections. Architect 🟠 #4: the
 * passport block is rendered only when `detail.passport !== null` (server
 * is sole authority on the reveal gate — null === piiRevealed=false).
 * Individual fields inside the passport object are independently
 * nullable (sparse persistence — provider may surface name without DOB
 * etc.); the `?? '—'` fallback handles those.
 *
 * Document thumbnails are decorative placeholders matching the mockup —
 * the real signed-URL plumbing for passport images is deferred to ADMIN-15+
 * (out of v1 scope per ticket Output files).
 */

const DocumentThumb = ({ caption }: { caption: string }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        flex: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          height: 110,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          background:
            'repeating-linear-gradient(45deg, rgba(0,0,0,0.04), rgba(0,0,0,0.04) 10px, rgba(0,0,0,0.02) 10px, rgba(0,0,0,0.02) 20px)',
        }}
      >
        <ImageOutlinedIcon sx={{ fontSize: 28 }} />
      </Box>
      <Box
        sx={{
          p: '8px 10px',
          fontSize: 11,
          color: tokens.text.secondary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {caption}
        <Chip size="small" color="success" label={t('inspectors.kycTab.documents.ok')} />
      </Box>
    </Box>
  );
};

export const KycTab = ({ inspector }: { inspector: AdminInspectorDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const { kyc, passport } = inspector;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <SectionTitle>{t('inspectors.kycTab.verificationSection')}</SectionTitle>
        {kyc.currentSessionId === null ? (
          <Typography variant="body2" color="text.secondary">
            {t('inspectors.kycTab.noSession')}
          </Typography>
        ) : (
          <InfoGrid>
            <InfoItem label={t('inspectors.kycTab.fields.status')}>
              <KycSessionBadge sessionStatus={kyc.sessionStatus} />
            </InfoItem>
            <InfoItem label={t('inspectors.kycTab.fields.provider')}>
              {t('inspectors.kycTab.providerName')}
            </InfoItem>
            <InfoItem label={t('inspectors.kycTab.fields.passedAt')}>
              {formatDate(kyc.lastPassedAt)}
            </InfoItem>
            <InfoItem label={t('inspectors.kycTab.fields.document')}>
              {t('inspectors.kycTab.documentType')}
            </InfoItem>
            <InfoItem label={t('inspectors.kycTab.fields.country')}>
              {passport?.country ?? inspector.city}
            </InfoItem>
            {kyc.resetReason ? (
              <InfoItem label={t('inspectors.kycTab.fields.resetReason')}>
                {kyc.resetReason}
              </InfoItem>
            ) : null}
          </InfoGrid>
        )}
      </Box>

      <Box>
        <SectionTitle>{t('inspectors.kycTab.documentsSection')}</SectionTitle>
        {passport === null ? (
          <Alert severity="info" variant="outlined">
            {t('inspectors.kycTab.passportGated')}
          </Alert>
        ) : (
          <>
            <InfoGrid>
              <InfoItem label={t('inspectors.kycTab.fields.passportFullName')}>
                {[passport.firstName, passport.lastName].filter(Boolean).join(' ') || '—'}
              </InfoItem>
              <InfoItem label={t('inspectors.kycTab.fields.passportNumber')}>
                {passport.number ?? '—'}
              </InfoItem>
              <InfoItem label={t('inspectors.kycTab.fields.passportDateOfBirth')}>
                {formatDate(passport.dateOfBirth)}
              </InfoItem>
              <InfoItem label={t('inspectors.kycTab.fields.passportExpiryDate')}>
                {formatDate(passport.expiryDate)}
              </InfoItem>
            </InfoGrid>
            <Box sx={{ display: 'flex', gap: 1.75, mt: 2 }}>
              <DocumentThumb caption={t('inspectors.kycTab.documents.passport')} />
              <DocumentThumb caption={t('inspectors.kycTab.documents.selfie')} />
              <DocumentThumb caption={t('inspectors.kycTab.documents.selfieWithDoc')} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
